import React, { useEffect } from 'react';
import { Link, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { GraduationCapIcon, BookOpenIcon, HeartIcon, UsersIcon, ShieldCheckIcon, ClockIcon, DollarSignIcon, AwardIcon, CheckCircleIcon, ArrowRightIcon, StarIcon, QuoteIcon, CheckIcon } from 'lucide-react';
import { getFindDegreesTemplate } from '../data/findDegreesTemplates';
import { getProgramTemplate, programsIndexSlug } from '../data/programTemplates';
import { ProgramTemplatePage } from './ProgramTemplatePage';
import { ProgramDomainsPage } from './ProgramDomainsPage';
import { usePageMeta } from '../hooks/usePageMeta';
const benefitIconMap: Record<string, React.ElementType> = {
  GraduationCapIcon,
  BookOpenIcon,
  HeartIcon,
  UsersIcon,
  ShieldCheckIcon,
  ClockIcon,
  DollarSignIcon,
  AwardIcon
};
/**
 * Resolves a slug to the right template: the program index, a domain page, an
 * individual program page, or the original lead-gen landing template. Explicit
 * routes are declared in App.tsx (so each URL shows up on its own), which means
 * the slug is read from the path when there is no `:template` param to read.
 *
 * Program content lives under `/program-information/`; anything reaching the
 * legacy `/find-degrees/` path with a program slug is redirected there so old
 * links keep working.
 */
export function FindDegreesPage() {
  const {
    template
  } = useParams<{
    template: string;
  }>();
  const location = useLocation();
  const pathSlug = location.pathname.split('/').filter(Boolean).pop() || '';
  const slug = (template || pathSlug).toLowerCase();
  const isLegacyPath = location.pathname.startsWith('/find-degrees/');
  const program = getProgramTemplate(slug);
  const isProgramContent = slug === programsIndexSlug || !!program;
  if (isLegacyPath && isProgramContent) {
    return <Navigate to={`/program-information/${slug}${location.search}`} replace data-id="element-1432" data-mp="2r9275fnm9d82" />;
  }
  if (slug === programsIndexSlug) {
    return <ProgramDomainsPage data-id="element-1433" data-mp="2ho2lt2bvvttz" />;
  }
  if (program) {
    return <ProgramTemplatePage key={program.slug} program={program} data-id="element-1434" data-mp="2mlualnh8ezty" />;
  }
  return <FindDegreesLandingTemplate data-id="element-1435" data-mp="28lb9l0b6ou1a" />;
}
function FindDegreesLandingTemplate() {
  const {
    template
  } = useParams<{
    template: string;
  }>();
  const navigate = useNavigate();
  const data = template ? getFindDegreesTemplate(template) : undefined;
  usePageMeta(data?.metaTitle || 'Find Your Degree | OnlineCollegeGuide', data?.metaDescription || 'Find accredited online degree programs that fit your goals.', {
    ogType: 'website'
  });
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [template]);
  if (!data) {
    return <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4" data-id="element-1436" data-mp="2vjo8v3yk43jr">
        <h1 className="text-3xl font-bold text-navy mb-4" data-id="element-1437" data-mp="3j9s9d2gg7n6j">Page Not Found</h1>
        <p className="text-gray-600 mb-8" data-id="element-1438" data-mp="3o2levsjqqzjo">
          We couldn't find the page you're looking for.
        </p>
        <Link to="/" className="text-white bg-navy px-6 py-2 rounded-md hover:bg-navy-light transition-colors" data-id="element-1439" data-mp="1cl8e7vi8ac97">
          Return Home
        </Link>
      </div>;
  }
  const accent = data.accentColor;
  const accentDark = data.accentColorDark;
  const handleCta = () => {
    navigate(data.ctaHref);
  };
  return <main className="min-h-screen bg-white" data-id="element-1440" data-mp="tnywqjwgr8ul">
      {/* Minimal Top Bar */}
      <div className="bg-white border-b border-gray-100" data-id="element-1441" data-mp="tq1igrql2fti">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between" data-id="element-1442" data-mp="opxd9xv4l7il">
          <Link to="/" className="flex items-center" data-id="element-1443" data-mp="13n69p5zad1h6">
            <img src="https://cdn.magicpatterns.com/uploads/98gXraqnTFT6vsk8BtTnBr/FOC_Logo_(1).png" alt="findonlinecollege.com" className="h-10 w-auto object-contain" data-id="element-1444" data-mp="yn1qh86ztsn9" />
          </Link>
          <div className="hidden sm:flex items-center text-xs font-semibold text-gray-500 tracking-wider uppercase" data-id="element-1445" data-mp="2a15jab1uhaqq">
            <ShieldCheckIcon className="w-4 h-4 mr-1.5 text-green-600" data-id="element-1446" data-mp="jmo4qaclc3r9" />
            Trusted by 2M+ Students
          </div>
        </div>
      </div>

      {/* Hero - light theme matching reference layout */}
      <section className="bg-gray-50 relative overflow-hidden" data-id="element-1447" data-mp="skgktgq4zffp">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20" data-id="element-1448" data-mp="xlxiz4h8knsl">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center" data-id="element-1449" data-mp="12kh5p73nm3m0">
            {/* Left: Copy */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5
          }} data-id="element-1450" data-mp="17i8ye3c4htr9">
              <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-[11px] font-semibold uppercase tracking-widest text-white mb-6" style={{
              backgroundColor: '#14479E'
            }} data-id="element-1451" data-mp="1lb8d4342zjxr">
                <span className="w-1.5 h-1.5 rounded-full" style={{
                backgroundColor: '#8FBEF5'
              }} data-id="element-1452" data-mp="1vendmftihse1" />
                {data.heroEyebrowBadge || data.eyebrow}
              </span>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] text-gray-900 mb-6 tracking-tight" data-id="element-1453" data-mp="1mfsa5bhuqwef">
                {data.heroHeadlinePrefix && <>{data.heroHeadlinePrefix} </>}
                {data.heroHeadlineHighlight && <span style={{
                color: '#14479E'
              }} data-id="element-1454" data-mp="29d3kc6rdjiw6">
                    {data.heroHeadlineHighlight}
                  </span>}
                {data.heroHeadlineSuffix && <>
                    <br data-id="element-1455" data-mp="2x2ofrp4ps8mc" />
                    {data.heroHeadlineSuffix}
                  </>}
                {!data.heroHeadlinePrefix && !data.heroHeadlineHighlight && !data.heroHeadlineSuffix && data.headline}
              </h1>

              <p className="text-base sm:text-lg text-gray-600 max-w-xl leading-relaxed" data-id="element-1456" data-mp="lfclspvs2glg">
                {data.subheadline}
              </p>
            </motion.div>

            {/* Right: Step indicator + Card */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.96
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} data-id="element-1457" data-mp="ce4n46pcndqu">
              {/* Step Indicator */}
              <div className="flex items-center justify-between mb-5 px-2 sm:px-4" data-id="element-1458" data-mp="1wvaw0x2oz8o9">
                <div className="flex items-center gap-2.5" data-id="element-1459" data-mp="21v5oh52486bf">
                  <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{
                  backgroundColor: '#1668E0'
                }} data-id="element-1460" data-mp="21sjgx5f85qt7">
                    <CheckIcon className="w-4 h-4 text-white" strokeWidth={3} data-id="element-1461" data-mp="3w27yt3kukcmz" />
                  </div>
                  <span className="text-sm font-semibold text-gray-900" data-id="element-1462" data-mp="3rb9e7rstssdx">
                    {data.heroStep1Label || 'Profile Complete'}
                  </span>
                </div>
                <div className="flex-1 h-px bg-gray-300 mx-3 sm:mx-4" data-id="element-1463" data-mp="4w6s177x0575" />
                <div className="flex items-center gap-2.5" data-id="element-1464" data-mp="4y9g3lpibo98">
                  <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{
                  backgroundColor: '#14479E'
                }} data-id="element-1465" data-mp="iuwoqlqto2uz">
                    <span className="text-xs font-bold text-white" data-id="element-1466" data-mp="2knkv33yrj6ez">2</span>
                  </div>
                  <span className="text-sm font-semibold text-gray-500" data-id="element-1467" data-mp="2fq2j14pg8b2r">
                    {data.heroStep2Label || 'Confirm Email'}
                  </span>
                </div>
              </div>

              {/* Card */}
              <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 sm:p-10 text-center" data-id="element-1468" data-mp="23kvl04oqmjxo">
                <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-2" data-id="element-1469" data-mp="28joz9zbumeyw">
                  {data.heroCardHeading || data.headline}
                </h2>
                {data.heroCardSubtext && <p className="text-sm font-semibold mb-6" style={{
                color: '#14479E'
              }} data-id="element-1470" data-mp="1furiy1r76s8s">
                    {data.heroCardSubtext}
                  </p>}
                <button type="button" onClick={handleCta} className="group inline-flex items-center justify-center h-[52px] px-8 bg-primary hover:bg-primary-hover active:bg-secondary text-white font-semibold rounded-full text-base leading-none shadow-ds-md transition-colors duration-150 ease-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary" data-id="element-1471" data-mp="2w9jdak7wm9kq">
                  {data.ctaLabel}
                  <ArrowRightIcon className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1" data-id="element-1472" data-mp="awnulyokpth2" />
                </button>
                {data.heroCardHelper && <p className="mt-6 text-xs sm:text-sm text-gray-500 leading-relaxed max-w-xs mx-auto" data-id="element-1473" data-mp="5uyjyk59jhti">
                    {data.heroCardHelper}
                  </p>}
              </div>

              {/* Trust strip below card */}
              <div className="mt-6 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-gray-500" data-id="element-1474" data-mp="3o3xzd6zx45p4">
                {data.trustItems.map(item => <div key={item} className="flex items-center" data-id="element-1475" data-mp="1mbt1zfzngg70">
                    <CheckCircleIcon className="w-3.5 h-3.5 text-green-600 mr-1.5 flex-shrink-0" data-id="element-1476" data-mp="1m9gkt51pu9v0" />
                    {item}
                  </div>)}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 lg:py-20 bg-white" data-id="element-1477" data-mp="364ew1472fohf">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1478" data-mp="323mnkd60pnm7">
          <div className="text-center max-w-2xl mx-auto mb-12" data-id="element-1479" data-mp="3218ta6j5wffp">
            <h2 className="text-2xl sm:text-3xl font-bold mb-3" style={{
            color: accent
          }} data-id="element-1480" data-mp="2eetxgxc2u9zi">
              {data.benefitsHeading}
            </h2>
            <div className="w-16 h-1 mx-auto rounded-full" style={{
            backgroundColor: '#1668E0'
          }} data-id="element-1481" data-mp="2n7nep0b33jg0" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1482" data-mp="1uogt7i2hdyon">
            {data.benefits.map((benefit, i) => {
            const Icon = benefitIconMap[benefit.icon] || BookOpenIcon;
            return <motion.div key={i} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.4,
              delay: i * 0.05
            }} className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-md transition-shadow" data-id="element-1483" data-mp="1gi6i4vxks5s2" data-mpi={i}>
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4" style={{
                backgroundColor: `${accent}15`
              }} data-id="element-1484" data-mp="1pexoc15ud10d">
                    <Icon className="w-6 h-6" style={{
                  color: accent
                }} data-id="element-1485" data-mp="ixk7pajobud1" />
                  </div>
                  <h3 className="text-lg font-bold mb-2" style={{
                color: accent
              }} data-id="element-1486" data-mp="2gl4m44mg3xxt">
                    {benefit.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed" data-id="element-1487" data-mp="26sfjlz8vhuem">
                    {benefit.description}
                  </p>
                </motion.div>;
          })}
          </div>
        </div>
      </section>

      {/* Featured Schools */}
      <section className="py-14 bg-gray-50 border-y border-gray-200" data-id="element-1488" data-mp="1sv0qokjev4hw">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1489" data-mp="1nw7dyuby7his">
          <h2 className="text-center text-sm font-bold uppercase tracking-widest mb-8 text-gray-500" data-id="element-1490" data-mp="3n8i5nj5r2t1b">
            {data.schoolsHeading}
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4" data-id="element-1491" data-mp="3iaqe4q90p8xn">
            {data.schoolNames.map(name => <div key={name} className="bg-white border border-gray-200 rounded-lg p-4 text-center flex items-center justify-center min-h-[80px]" data-id="element-1492" data-mp="3pqvcb25n5bmj">
                <span className="text-sm font-semibold leading-tight" style={{
              color: accent
            }} data-id="element-1493" data-mp="3fgaor8760ic">
                  {name}
                </span>
              </div>)}
          </div>
        </div>
      </section>

      {/* Testimonial */}
      {data.testimonial && <section className="py-16 lg:py-20 bg-white" data-id="element-1494" data-mp="3grgpqdslhl7b">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1495" data-mp="3lr31eisryte0">
            <QuoteIcon className="w-12 h-12 mx-auto mb-6 opacity-30" style={{
          color: accent
        }} data-id="element-1496" data-mp="3lp01n71tllw3" />
            <div className="flex items-center justify-center gap-1 mb-4" data-id="element-1497" data-mp="10zkf4o9mwjc9">
              {[0, 1, 2, 3, 4].map(i => <StarIcon key={i} className="w-5 h-5 fill-current" style={{
            color: '#5FA0EE'
          }} data-id="element-1498" data-mp="yojl84rntzbt" />)}
            </div>
            <p className="text-xl sm:text-2xl font-medium leading-relaxed mb-6 italic" style={{
          color: accent
        }} data-id="element-1499" data-mp="13l0lfxc4w459">
              "{data.testimonial.quote}"
            </p>
            <p className="font-bold text-gray-900" data-id="element-1500" data-mp="1dj7wy0nnltzv">{data.testimonial.name}</p>
            <p className="text-sm text-gray-500" data-id="element-1501" data-mp="1ra2zywbh1gvj">{data.testimonial.detail}</p>
          </div>
        </section>}

      {/* Bottom CTA */}
      <section className="py-16 lg:py-20" style={{
      backgroundColor: accentDark
    }} data-id="element-1502" data-mp="1medw4gsf5dtf">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1503" data-mp="1wah9kisq0yvo">
          <h2 className="text-2xl sm:text-4xl font-extrabold text-white mb-4 leading-tight" data-id="element-1504" data-mp="1w8e9t71rnrdr">
            {data.bottomCtaHeading}
          </h2>
          <p className="text-base sm:text-lg text-gray-300 mb-8 leading-relaxed" data-id="element-1505" data-mp="2jwkvn900r2ha">
            {data.bottomCtaBody}
          </p>
          <button type="button" onClick={handleCta} className="group inline-flex items-center justify-center h-[52px] px-8 bg-primary hover:bg-primary-hover active:bg-secondary text-white font-semibold rounded-full text-base leading-none shadow-ds-md transition-colors duration-150 ease-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary" data-id="element-1506" data-mp="25xl701cianl4">
            {data.bottomCtaLabel}
            <ArrowRightIcon className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1" data-id="element-1507" data-mp="1s781e5n37kyy" />
          </button>
          <p className="mt-5 text-xs text-gray-400 uppercase tracking-widest" data-id="element-1508" data-mp="1d9xp4u2meb26">
            Complementary • No Obligation • Takes 2 Minutes
          </p>
        </div>
      </section>

      {/* Footer mini */}
      <footer className="bg-gray-900 text-gray-400 text-xs py-6" data-id="element-1509" data-mp="z5qfrp88bzq5">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-2" data-id="element-1510" data-mp="uc5dhkzlsjl5">
          <p data-id="element-1511" data-mp="pevqsd3lmm10">
            &copy; {new Date().getFullYear()} OnlineCollegeGuide. All rights
            reserved.
          </p>
          <p className="max-w-3xl mx-auto leading-relaxed" data-id="element-1512" data-mp="kgan23p51k7t">
            Advertiser Disclosure: OnlineCollegeGuide.com is an
            advertising-supported site. Featured schools compensate us, which
            does not influence our editorial content.
          </p>
          <div className="flex justify-center gap-4 pt-2" data-id="element-1513" data-mp="183ah0y63dw0r">
            <Link to="/privacy-policy" className="hover:text-white transition-colors" data-id="element-1514" data-mp="1358z4gkek2qv">
              Privacy Policy
            </Link>
            <Link to="/terms-and-conditions" className="hover:text-white transition-colors" data-id="element-1515" data-mp="3svuc0qis0gh2">
              Terms of Service
            </Link>
          </div>
        </div>
      </footer>
    </main>;
}